import { useEffect, useRef, useState } from 'react';

const DURATION_MS = 900;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/**
 * Count-up (~900ms ease-out) from the previously shown value (0 on mount) to
 * the new one. Returns the final value immediately when the user prefers
 * reduced motion.
 */
export function useAnimatedNumber(value: number): number {
  const [animatedValue, setAnimatedValue] = useState(() => (prefersReducedMotion() ? value : 0));
  const fromRef = useRef(animatedValue);

  useEffect(() => {
    if (!Number.isFinite(value) || prefersReducedMotion()) {
      fromRef.current = value;
      setAnimatedValue(value);
      return;
    }

    const from = fromRef.current;
    if (from === value) return;

    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / DURATION_MS, 1);
      const next = from + (value - from) * easeOutCubic(progress);
      fromRef.current = next;
      setAnimatedValue(progress >= 1 ? value : next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return animatedValue;
}
